import { FormikContext, getIn } from 'formik'
import React, { useContext } from 'react'
import { FlyOutListingData } from '../../../../types/flyout'
import { Sections } from '../../../../modules/flyOutOnboarding/reducer'
import {
  getSectionStatus,
  StatusIcon,
  StatusIconType,
} from '../../StepStatusIcon'
import { DataFeedsOwnS3BucketChoice } from '../types'
import { useStyles } from '../FlyOutArnAndDataFeedsWrapper.styles'
import Persona from '../../Persona/Persona'
import { CloudType } from '../../../../types'

export type FlyOutCloudSettingsStepSection =
  | 'cloudPartnerDetails'
  | 'createStacks'

export type CreateStacksStepStatusIconProps = {
  section: FlyOutCloudSettingsStepSection
  dataFeedsInOwnS3Bucket?: DataFeedsOwnS3BucketChoice
  statusOverride?: StatusIconType
}

type FormikConnectionStatus = {
  arnConnectionStatus?: string
  failedConnectionField?: string
  dataFeedsConnectionStatus?: string
  combinedConnectionStatus?: string
}

type FlyOutCloudSettingsStepFormikBag = {
  values: Sections | FlyOutListingData
  errors: unknown
  touched: unknown
  status?: unknown
}

const hasTouchedFieldError = (
  errors: unknown,
  touched: unknown,
  path: string
): boolean => {
  const error = getIn(errors, path)
  const isTouched = getIn(touched, path)
  return Boolean(isTouched && error)
}

const hasValidationErrors = (
  errors: unknown,
  touched: unknown,
  paths: string[]
): boolean => paths.some(path => hasTouchedFieldError(errors, touched, path))

const isNonEmptyString = (value: unknown): boolean =>
  typeof value === 'string' && value.trim().length > 0

const isNonEmptyArray = (value: unknown): boolean =>
  Array.isArray(value) && value.length > 0

const isConnectionFailure = (status?: string): boolean => status === 'failure'

const isConnectionSuccess = (status?: string): boolean => status === 'success'

const resolveOnboardingPartnerDetailsStatus = (
  formik: FlyOutCloudSettingsStepFormikBag
): { sectionCompleted: boolean; doesSectionHaveErrors: boolean } => {
  const cloudSettings = getIn(formik.values, 'cloudSettings') as
    | Record<string, unknown>
    | undefined

  const validationPaths = [
    'cloudSettings.cloudAccountId',
    'cloudSettings.cloudAccountName',
    'cloudSettings.countryRegistered',
    'cloudSettings.currencies',
    'labraPartnerDetails.partnershipType',
  ]

  const sectionCompleted =
    isNonEmptyString(cloudSettings?.cloudAccountId) &&
    isNonEmptyString(cloudSettings?.cloudAccountName) &&
    isNonEmptyString(cloudSettings?.countryRegistered) &&
    isNonEmptyArray(cloudSettings?.currencies) &&
    isNonEmptyArray(getIn(formik.values, 'labraPartnerDetails.partnershipType'))

  return {
    sectionCompleted,
    doesSectionHaveErrors: hasValidationErrors(
      formik.errors,
      formik.touched,
      validationPaths
    ),
  }
}

const resolveListingPartnerDetailsStatus = (
  formik: FlyOutCloudSettingsStepFormikBag
): { sectionCompleted: boolean; doesSectionHaveErrors: boolean } => {
  const values = formik.values as FlyOutListingData

  const validationPaths = [
    'awsAccountId',
    'awsAccountName',
    'countryRegistered',
    'currencies',
    'sellerType',
  ]

  const sectionCompleted =
    isNonEmptyString(values.awsAccountId) &&
    isNonEmptyString(values.awsAccountName) &&
    isNonEmptyString(values.countryRegistered) &&
    isNonEmptyArray(values.currencies) &&
    isNonEmptyArray(values.sellerType)

  return {
    sectionCompleted,
    doesSectionHaveErrors: hasValidationErrors(
      formik.errors,
      formik.touched,
      validationPaths
    ),
  }
}

const resolveCloudSettingsCreateStacksStatus = (
  cloudSettings: Record<string, unknown>,
  formik: FlyOutCloudSettingsStepFormikBag,
  isOwnS3BucketYes: boolean
): { sectionCompleted: boolean; doesSectionHaveErrors: boolean } => {
  const formikStatus = (formik.status ?? {}) as FormikConnectionStatus
  const testConnectionStatus = cloudSettings.testConnectionStatus as
    | string
    | undefined
  const dataFeedsConnectionTested =
    cloudSettings.dataFeedsConnectionTested === true
  const dataFeedsConnectionTestError = getIn(
    formik.errors,
    'cloudSettings.dataFeedsConnectionTested'
  )

  const validationPaths = [
    'cloudSettings.cloudRoleArn',
    'cloudSettings.cloudCasS3BucketName',
    'cloudSettings.cloudKmsKeyArn',
    'cloudSettings.cloudCasSnsTopic',
  ]

  const hasFieldValidationErrors = hasValidationErrors(
    formik.errors,
    formik.touched,
    validationPaths
  )

  const hasConnectionTestFailure =
    isConnectionFailure(testConnectionStatus) ||
    isConnectionFailure(formikStatus.dataFeedsConnectionStatus) ||
    isConnectionFailure(formikStatus.combinedConnectionStatus) ||
    Boolean(dataFeedsConnectionTestError) ||
    Boolean(formikStatus.failedConnectionField)

  const sectionCompleted = isOwnS3BucketYes
    ? isConnectionSuccess(testConnectionStatus) && dataFeedsConnectionTested
    : isConnectionSuccess(testConnectionStatus)

  return {
    sectionCompleted,
    doesSectionHaveErrors: hasConnectionTestFailure || hasFieldValidationErrors,
  }
}

const resolveListingCreateStacksStatus = (
  formik: FlyOutCloudSettingsStepFormikBag,
  isOwnS3BucketYes: boolean
): { sectionCompleted: boolean; doesSectionHaveErrors: boolean } => {
  const formikStatus = (formik.status ?? {}) as FormikConnectionStatus

  const validationPaths = [
    'sellerMetadata.awsRoleArn',
    'sellerMetadata.awsCasS3BucketName',
    'sellerMetadata.awsKmsKeyArn',
    'sellerMetadata.awsCasSnsTopic',
  ]

  const hasFieldValidationErrors = hasValidationErrors(
    formik.errors,
    formik.touched,
    validationPaths
  )

  const hasConnectionTestFailure =
    isConnectionFailure(formikStatus.arnConnectionStatus) ||
    isConnectionFailure(formikStatus.dataFeedsConnectionStatus) ||
    isConnectionFailure(formikStatus.combinedConnectionStatus) ||
    Boolean(formikStatus.failedConnectionField)

  const sectionCompleted = isOwnS3BucketYes
    ? isConnectionSuccess(formikStatus.arnConnectionStatus) &&
      isConnectionSuccess(formikStatus.dataFeedsConnectionStatus)
    : isConnectionSuccess(formikStatus.combinedConnectionStatus)

  return {
    sectionCompleted,
    doesSectionHaveErrors: hasConnectionTestFailure || hasFieldValidationErrors,
  }
}

const resolveCloudPartnerDetailsStatus = (
  formik: FlyOutCloudSettingsStepFormikBag
): { sectionCompleted: boolean; doesSectionHaveErrors: boolean } => {
  const cloudSettings = getIn(formik.values, 'cloudSettings')
  return cloudSettings !== undefined
    ? resolveOnboardingPartnerDetailsStatus(formik)
    : resolveListingPartnerDetailsStatus(formik)
}

const resolveCreateStacksStatus = (
  formik: FlyOutCloudSettingsStepFormikBag,
  dataFeedsInOwnS3Bucket: DataFeedsOwnS3BucketChoice
): { sectionCompleted: boolean; doesSectionHaveErrors: boolean } => {
  const cloudSettings = getIn(formik.values, 'cloudSettings')
  const isOwnS3BucketYes = dataFeedsInOwnS3Bucket === 'yes'

  return cloudSettings !== undefined
    ? resolveCloudSettingsCreateStacksStatus(
        cloudSettings as Record<string, unknown>,
        formik,
        isOwnS3BucketYes
      )
    : resolveListingCreateStacksStatus(formik, isOwnS3BucketYes)
}

export const CreateStacksStepStatusIcon: React.FC<
  CreateStacksStepStatusIconProps
> = ({ section, dataFeedsInOwnS3Bucket = 'no', statusOverride }) => {
  const formik = useContext(FormikContext) as
    | FlyOutCloudSettingsStepFormikBag
    | undefined

  if (statusOverride) {
    return <StatusIcon status={statusOverride} />
  }

  if (!formik) {
    return <StatusIcon status="standBy" />
  }

  const { sectionCompleted, doesSectionHaveErrors } =
    section === 'cloudPartnerDetails'
      ? resolveCloudPartnerDetailsStatus(formik)
      : resolveCreateStacksStatus(formik, dataFeedsInOwnS3Bucket)

  const status = getSectionStatus(sectionCompleted, doesSectionHaveErrors)

  return <StatusIcon status={status} />
}

export const CreateStacksSectionActions: React.FC = () => {
  const { classes } = useStyles()

  return (
    <div
      className={classes.personaSection}
      data-testid="create-stacks-persona-section"
    >
      <Persona memberType="admin" cloudType={CloudType.AWS} />
      <div className={classes.personaVerticalDivider} />
    </div>
  )
}
